// KontaktstatusPopup.tsx — „Kontaktstatus wählen" (User 28.09.2026): EIN
// Popup für die Status-Pille der Akte (ändern) und den Spaltenfilter
// „K-Status" der Kontaktliste (filtern).
//
//   [ Status oder Rolle suchen …            ]
//   HAUPTSTATUS   ( Kunde ) ( Interessent ) ( Sonstige )
//   SONSTIGE — ROLLEN
//   [x] Vertrieb und Netzwerk            ← ganzer Bereich
//       [x] Vermittler/Tippgeber
//       [ ] Empfehlungsgeber …
//
// Akte: genau ein Hauptstatus (Radio). Filter (seit 02.10.2026): beliebig
// viele Hauptstatus (Häkchen, auch keiner) — „Kunde + Interessent" oder
// „Sonstige + Kunde". Dazu jeweils beliebig viele Rollen.
// Die Suche filtert Hauptstatus, Bereiche und Rollen, umlaut-tolerant. Die
// Logik steckt DOM-frei in `kontaktstatusWahl.ts`.

import * as React from "react";
import type { RollenBereich } from "../../data/kontaktRollen";
import { rolleZu } from "../../data/kontaktRollen";
import {
  HAUPT_STATI,
  bereichUmschalten,
  bereichZustand,
  gefilterteBereiche,
  gleicheFilterWahl,
  hauptUmschalten,
  rolleUmschalten,
  trifft,
  type HauptStatus,
  type KStatusFilterWahl,
  type KontaktstatusWahl,
} from "./kontaktstatusWahl";

type GemeinsameProps = {
  /** Wählbare Bereiche (bereits nach Kontakttyp/Recht gefiltert). */
  bereiche: RollenBereich[];
};

/** Akte: genau ein Hauptstatus (Pflicht), Knöpfe Abbrechen/Speichern. */
export type KontaktstatusPopupAkteProps = GemeinsameProps & {
  modus: "akte";
  start: KontaktstatusWahl;
  onAnwenden: (w: KontaktstatusWahl) => void;
  /** Schließen ohne Speichern. */
  onAbbrechen?: () => void;
  /** Speichern läuft. */
  speichert?: boolean;
};

/** Filter: beliebig viele Hauptstatus (auch keiner), Knöpfe Zurücksetzen/Anwenden. */
export type KontaktstatusPopupFilterProps = GemeinsameProps & {
  modus: "filter";
  start: KStatusFilterWahl;
  onAnwenden: (w: KStatusFilterWahl) => void;
  /** Filter entfernen. */
  onZuruecksetzen?: () => void;
};

export type KontaktstatusPopupProps = KontaktstatusPopupAkteProps | KontaktstatusPopupFilterProps;

/** Innerer Zustand: immer als Liste, die Akte hält höchstens einen Eintrag. */
const alsFilterWahl = (p: KontaktstatusPopupProps): KStatusFilterWahl =>
  p.modus === "filter" ? p.start : { haupt: p.start.haupt ? [p.start.haupt] : [], rollen: p.start.rollen };

const FOLGE_HINWEIS: Record<string, string> = {
  unternehmen: "das Unternehmen",
  verein: "den Verein",
  person: "die Person",
};

function BereichHaken({
  zustand,
  onChange,
  label,
}: {
  zustand: "alle" | "teils" | "keine";
  onChange: () => void;
  label: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = zustand === "teils";
  }, [zustand]);
  return (
    <label className="rd-pop-check kst-bereich">
      <input ref={ref} type="checkbox" checked={zustand === "alle"} onChange={onChange} aria-label={`Bereich ${label}`} />
      <span>{label}</span>
    </label>
  );
}

export function KontaktstatusPopup(props: KontaktstatusPopupProps) {
  const { modus, bereiche } = props;
  const [start] = React.useState<KStatusFilterWahl>(() => alsFilterWahl(props));
  const [wahl, setWahl] = React.useState<KStatusFilterWahl>(start);
  const [suche, setSuche] = React.useState("");

  // Rollen, die der Kontakt/Filter trägt, die der Katalog aber nicht (mehr)
  // anbietet — Altbestand oder für diesen Kontakttyp nicht wählbar. Sie
  // bleiben sichtbar, damit man sie abwählen kann. Aus dem START berechnet,
  // damit eine abgewählte Zeile nicht unter dem Finger verschwindet.
  const [weitere] = React.useState<string[]>(() => {
    const bekannt = new Set(bereiche.flatMap((b) => b.rollen.map((r) => r.value)));
    return start.rollen.filter((r) => !bekannt.has(r));
  });
  const alleBereiche = React.useMemo<RollenBereich[]>(
    () =>
      weitere.length
        ? [
            ...bereiche,
            {
              titel: "Weitere Rollen",
              rollen: weitere.map((v) => ({ value: v, label: rolleZu(v)?.label ?? v, typ: "beide" as const })),
            },
          ]
        : bereiche,
    [bereiche, weitere],
  );

  const sichtbar = gefilterteBereiche(alleBereiche, suche);
  const hauptSichtbar = HAUPT_STATI.filter((h) => trifft(h, suche));

  const hauptWaehlen = (h: HauptStatus) =>
    setWahl((w) => (modus === "filter" ? hauptUmschalten(w, h) : { ...w, haupt: [h] }));

  const unveraendert = gleicheFilterWahl(wahl, start);
  const anwenden = () => {
    if (props.modus === "filter") props.onAnwenden(wahl);
    else props.onAnwenden({ haupt: wahl.haupt[0] ?? null, rollen: wahl.rollen });
  };
  const onAbbrechen = props.modus === "akte" ? props.onAbbrechen : undefined;
  const neueFolgen =
    modus === "akte"
      ? Array.from(
          new Set(
            wahl.rollen
              .filter((r) => !start.rollen.includes(r))
              .map((r) => rolleZu(r)?.folge)
              .filter((f): f is NonNullable<typeof f> => !!f),
          ),
        )
      : [];

  return (
    <div
      className="kst-popup"
      role="dialog"
      aria-label="Kontaktstatus wählen"
      onKeyDown={(e) => {
        if (e.key === "Escape" && modus === "akte") onAbbrechen?.();
      }}
    >
      <input
        className="rd-pop-input"
        type="search"
        placeholder="Status oder Rolle suchen …"
        aria-label="Status oder Rolle suchen"
        value={suche}
        autoFocus
        onChange={(e) => setSuche(e.target.value)}
      />

      <div className="kst-liste">
        {hauptSichtbar.length > 0 && (
          <>
            <div className="rd-pop-grouptitle">Hauptstatus</div>
            <div
              className="kst-haupt"
              role={modus === "filter" ? "group" : "radiogroup"}
              aria-label={modus === "filter" ? "Hauptstatus (mehrere möglich)" : "Hauptstatus"}
            >
              {hauptSichtbar.map((h) => (
                <button
                  key={h}
                  type="button"
                  role={modus === "filter" ? "checkbox" : "radio"}
                  aria-checked={wahl.haupt.includes(h)}
                  className={"rd-pop-opt kst-haupt-opt" + (wahl.haupt.includes(h) ? " on" : "")}
                  onClick={() => hauptWaehlen(h)}
                >
                  {h}
                </button>
              ))}
            </div>
          </>
        )}

        {sichtbar.length > 0 && <div className="rd-pop-grouptitle">Sonstige — Rollen</div>}
        {sichtbar.map((b) => {
          const werte = b.rollen.map((r) => r.value);
          return (
            <div key={b.titel} className="kst-gruppe">
              <BereichHaken
                label={b.titel}
                zustand={bereichZustand(wahl, werte)}
                onChange={() => setWahl((w) => bereichUmschalten(w, werte))}
              />
              {b.rollen.map((r) => (
                <label key={b.titel + "|" + r.value} className="rd-pop-check kst-rolle">
                  <input
                    type="checkbox"
                    checked={wahl.rollen.includes(r.value)}
                    onChange={() => setWahl((w) => rolleUmschalten(w, r.value))}
                  />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          );
        })}

        {hauptSichtbar.length === 0 && sichtbar.length === 0 && (
          <div className="rd-pop-empty">Nichts gefunden.</div>
        )}
      </div>

      {neueFolgen.length > 0 && (
        <div className="rd-pop-hint kst-hinweis">
          Die Rolle wird nur als Status gesetzt. {neueFolgen.map((f) => FOLGE_HINWEIS[f]).join(", ")} bitte unter
          „Verbindungen" erfassen.
        </div>
      )}

      <div className="rd-pop-foot kst-fuss">
        {modus === "filter" ? (
          <>
            <button
              type="button"
              className="rd-pop-btn"
              disabled={start.haupt.length === 0 && start.rollen.length === 0 && wahl.haupt.length === 0 && wahl.rollen.length === 0}
              onClick={() => props.modus === "filter" && props.onZuruecksetzen?.()}
            >
              Zurücksetzen
            </button>
            <button type="button" className="rd-pop-btn primary" onClick={anwenden}>
              Anwenden
            </button>
          </>
        ) : (
          <>
            <button type="button" className="rd-pop-btn" onClick={() => onAbbrechen?.()}>
              Abbrechen
            </button>
            <button
              type="button"
              className="rd-pop-btn primary"
              disabled={wahl.haupt.length === 0 || unveraendert || (props.modus === "akte" && props.speichert)}
              title={wahl.haupt.length === 0 ? "Bitte einen Hauptstatus wählen" : undefined}
              onClick={anwenden}
            >
              {props.modus === "akte" && props.speichert ? "Speichert …" : "Speichern"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
